<template>
  <v-card outlined class="metrics-series-chart">
    <div class="metrics-series-chart__header">
      <div class="metrics-series-chart__title">
        <v-icon small class="mr-2">mdi-chart-line</v-icon>
        Metrics series
        <span class="caption grey--text ml-2">
          One point per snapshot · the line breaks when engines or configuration change
        </span>
      </div>
      <v-spacer />
      <v-btn-toggle v-model="method" mandatory dense class="metrics-series-chart__method">
        <v-btn value="computed_subtraction" small text>Alerts Subtraction</v-btn>
        <v-btn value="alert_major" small text>Alert Major</v-btn>
      </v-btn-toggle>
      <v-btn
        v-if="canSave"
        small
        color="primary"
        depressed
        class="ml-2 metrics-series-chart__save"
        :loading="saving"
        :disabled="saving"
        @click="$emit('save-snapshot')"
      >
        <v-icon small left>mdi-camera-plus-outline</v-icon>
        Save snapshot
      </v-btn>
    </div>

    <v-skeleton-loader v-if="loading" type="image" height="220" class="ma-4" />

    <div v-else-if="!snapshots.length" class="metrics-series-chart__empty">
      <v-icon size="40" color="grey lighten-1">mdi-chart-timeline-variant</v-icon>
      <div class="subtitle-2 grey--text mt-2">No snapshots yet</div>
      <div class="caption grey--text">
        A snapshot is saved automatically when a group batch completes, or on demand with Save
        snapshot.
      </div>
    </div>

    <template v-else>
      <div class="metrics-series-chart__legend caption grey--text text--darken-1">
        <span v-if="breakCount" class="metrics-series-chart__breaks">
          <v-icon x-small color="warning">mdi-source-branch</v-icon>
          {{ breakCount }} series {{ breakCount === 1 ? 'break' : 'breaks' }} · points on different
          sides of a break are not comparable
        </span>
        <span v-else>Single series · every point is comparable</span>
      </div>
      <apexchart
        type="line"
        height="260"
        :options="chartOptions"
        :series="chartSeries"
        class="metrics-series-chart__chart"
      />
      <v-simple-table dense class="metrics-series-chart__table">
        <template #default>
          <thead>
            <tr>
              <th>Date</th>
              <th>Batch</th>
              <th>Trigger</th>
              <th class="text-right">Lies detected</th>
              <th class="text-right">False alarms</th>
              <th class="text-right">Correct</th>
              <th>Series</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="snapshot in orderedDesc"
              :key="snapshot._id || snapshot.id || snapshot.computed_at"
              class="metrics-series-chart__row"
              :class="{ 'metrics-series-chart__row--break': snapshot.series_break }"
            >
              <td class="text-no-wrap">{{ formatDate(snapshot.computed_at) }}</td>
              <td>{{ snapshot.batch || '—' }}</td>
              <td>
                <v-chip x-small outlined>{{
                  snapshot.trigger === 'auto' ? 'auto' : 'manual'
                }}</v-chip>
              </td>
              <td
                v-for="metric in metrics"
                :key="metric.key"
                class="text-right"
                :class="`${statusColor(metric.key, snapshot)}--text`"
              >
                {{ formatPct(metricValue(snapshot, metric.key, method)) }}
              </td>
              <td>
                <span class="metrics-series-chart__key" :title="snapshot.series_key || ''">
                  {{ shortKey(snapshot.series_key) }}
                </span>
                <v-chip
                  v-if="snapshot.series_break"
                  x-small
                  color="warning"
                  outlined
                  class="ml-1 metrics-series-chart__break-chip"
                  :title="breakTitle(snapshot)"
                >
                  series break
                </v-chip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </template>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import {
  SERIES_METRICS,
  buildApexSeries,
  describeSeriesInputs,
  markSeriesBreaks,
  metricStatus,
  metricStatusColor,
  metricValue,
} from '@/utils/metricsSeries';

/**
 * Snapshot series of a group (M4 contract 7): one apexcharts line per PO metric with the line
 * cut at every series break, target lines at 70 % / 2 %, and a table of snapshots (newest first).
 * The hover tooltip shows the batch, engine versions and configuration of the snapshot.
 */
export default {
  name: 'MetricsSeriesChart',

  components: { apexchart: VueApexCharts },

  props: {
    /** Snapshots of one group (any order); `series_break` is recomputed from `series_key`. */
    snapshots: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    canSave: { type: Boolean, default: true },
  },

  data() {
    return {
      method: 'computed_subtraction',
      metrics: SERIES_METRICS,
    };
  },

  computed: {
    ordered() {
      return markSeriesBreaks(this.snapshots);
    },
    orderedDesc() {
      return [...this.ordered].reverse();
    },
    breakCount() {
      return this.ordered.filter((snapshot) => snapshot.series_break).length;
    },
    chartSeries() {
      return buildApexSeries(this.ordered, { method: this.method }).map((series) => ({
        name: series.name,
        data: series.data,
      }));
    },
    chartOptions() {
      const colors = this.metrics.map((metric) => metric.color);
      return {
        chart: {
          id: 'metrics-series-chart',
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
          fontFamily: 'inherit',
        },
        colors,
        stroke: { width: 2, curve: 'straight' },
        markers: { size: 4, hover: { size: 6 } },
        dataLabels: { enabled: false },
        legend: { position: 'top', horizontalAlign: 'left' },
        xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
        yaxis: {
          min: 0,
          max: 100,
          tickAmount: 5,
          labels: { formatter: (value) => `${Math.round(value)}%` },
        },
        annotations: {
          yaxis: [
            {
              y: 70,
              borderColor: '#43a047',
              strokeDashArray: 4,
              label: {
                text: 'target 70%',
                position: 'left',
                textAnchor: 'start',
                style: { color: '#fff', background: '#43a047' },
              },
            },
            {
              y: 2,
              borderColor: '#e53935',
              strokeDashArray: 4,
              label: {
                text: 'target 2%',
                position: 'left',
                textAnchor: 'start',
                style: { color: '#fff', background: '#e53935' },
              },
            },
          ],
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: ({ dataPointIndex, w }) => this.tooltipHtml(dataPointIndex, w),
        },
        grid: { strokeDashArray: 3 },
      };
    },
  },

  methods: {
    metricValue,

    formatPct(value) {
      return value === null || value === undefined ? '—' : `${value}%`;
    },

    formatDate(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (!Number.isFinite(date.getTime())) return String(value);
      const pad = (n) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
        date.getHours()
      )}:${pad(date.getMinutes())}`;
    },

    shortKey(key) {
      return key ? String(key).slice(0, 8) : '—';
    },

    statusColor(metricKey, snapshot) {
      return metricStatusColor(
        metricStatus(metricKey, metricValue(snapshot, metricKey, this.method))
      );
    },

    breakTitle(snapshot) {
      const text = describeSeriesInputs(snapshot.series_inputs);
      return text ? `New series: ${text}` : 'New series: engines or configuration changed';
    },

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },

    /** Tooltip: batch, date, the three values, engine versions and config of the snapshot. */
    tooltipHtml(dataPointIndex, w) {
      const point = w?.config?.series?.[0]?.data?.[dataPointIndex];
      const meta = point && point.meta;
      if (!meta) return '';
      const values = (w.config.series || [])
        .map((series) => {
          const item = series.data[dataPointIndex];
          const value = item && item.y !== null && item.y !== undefined ? `${item.y}%` : '—';
          return `<div>${this.escapeHtml(series.name)}: <strong>${this.escapeHtml(value)}</strong></div>`;
        })
        .join('');
      const versions = meta.engine_versions
        ? Object.keys(meta.engine_versions)
            .map((name) => `${name} ${meta.engine_versions[name]}`)
            .join(' · ')
        : '';
      const config = meta.config
        ? [
            meta.config.version ? `config v${meta.config.version}` : null,
            meta.config.engines_hash ? String(meta.config.engines_hash).slice(0, 8) : null,
          ]
            .filter(Boolean)
            .join(' · ')
        : '';
      return `
        <div class="metrics-series-chart__tooltip">
          <div class="metrics-series-chart__tooltip-title">${this.escapeHtml(meta.batch || 'snapshot')}</div>
          <div class="metrics-series-chart__tooltip-date">${this.escapeHtml(this.formatDate(meta.computed_at))}</div>
          ${values}
          ${versions ? `<div class="metrics-series-chart__tooltip-meta">${this.escapeHtml(versions)}</div>` : ''}
          ${config ? `<div class="metrics-series-chart__tooltip-meta">${this.escapeHtml(config)}</div>` : ''}
          ${meta.series_break ? '<div class="metrics-series-chart__tooltip-break">series break</div>' : ''}
        </div>`;
    },
  },
};
</script>

<style scoped>
.metrics-series-chart {
  border-radius: 10px !important;
  margin-bottom: 32px;
}

.metrics-series-chart__header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px 4px;
}

.metrics-series-chart__title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #555;
}

.metrics-series-chart__method {
  text-transform: none;
}

.metrics-series-chart__legend {
  padding: 0 16px;
}

.metrics-series-chart__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  text-align: center;
}

.metrics-series-chart__table {
  background: transparent !important;
}

.metrics-series-chart__row--break td:first-child {
  border-left: 3px solid #fb8c00;
}

.metrics-series-chart__key {
  font-family: 'Roboto Mono', monospace;
  font-size: 11px;
  color: #616161;
}
</style>

<style>
/* Tooltip is rendered by apexcharts outside the component scope. */
.metrics-series-chart__tooltip {
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.4;
}

.metrics-series-chart__tooltip-title {
  font-weight: 700;
}

.metrics-series-chart__tooltip-date,
.metrics-series-chart__tooltip-meta {
  color: #757575;
  font-size: 11px;
}

.metrics-series-chart__tooltip-break {
  color: #fb8c00;
  font-weight: 700;
  font-size: 11px;
}
</style>
